// src/components/layout/CoachDock.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  No agenda badge and no "things only you can settle" card. Tyler:
//               no questions for Jazz on the system at all. With his 99 and his
//               39 settled, the badge was counting questions and a constant 3
//               for "things no table can know", so it never read below three
//               and always read as homework. The coach still captures anything
//               he volunteers; it just stops keeping score of what he has not.
//   2026-08-31  New. The coach is reachable from anywhere, not just its own tab.
//
// A coach you have to navigate to is a page. A coach that is always within
// reach is a colleague, and the difference is whether somebody asks the small
// question they would otherwise not bother with.
//
// WHAT IT IS NOT
//
// Not a chat window bolted to the corner of every screen. Those get dismissed
// once and never opened again, and on a phone they cover the thing you were
// reading. This is a door, not a room: one tap and you are in the coach with
// the question already framed by where you were standing.
//
// MOBILE FIRST, AND IT YIELDS
//
// It sits above the floating tab bar rather than beside it, clears the home
// indicator with the same safe-area inset the bar uses, and hides itself
// entirely on the coach page, because a button that takes you where you already
// are is clutter. It also hides while the roadmap step content is being read on
// a phone, since that is the one screen where a floating control genuinely gets
// in the way of the text.
//
// PROMPTS ARE ROLE AWARE
//
// A salesperson gets a drill on the step they are standing on. A manager gets
// help with a conversation they are about to have. An owner gets the question
// they are actually paying to have answered. Offering a salesperson's drill to
// an owner is how a good feature reads as not-for-me.

import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box, Flex, HStack, VStack, Text, Icon, IconButton, useDisclosure,
  Drawer, DrawerOverlay, DrawerContent, DrawerBody, usePrefersReducedMotion,
} from '@chakra-ui/react'
import { Sparkles, X } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { atLeast } from '../../lib/nav'

// The J|13 mark, small. Same lockup as the emails and the share cards, drawn as
// type inside a border so it needs no asset and inverts cleanly.
function CoachMark({ open, badge = 0 }) {
  return (
    <Box position="relative">
      {badge > 0 && !open && (
        <Flex
          position="absolute"
          top="-2px"
          right="-2px"
          zIndex={1}
          minW="20px"
          h="20px"
          px={1.5}
          borderRadius="full"
          bg="accent.500"
          color="onInk"
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight={700}
          border="2px solid"
          borderColor="bg"
          aria-hidden="true"
        >
          {badge}
        </Flex>
      )}
    <Flex
      boxSize="52px"
      borderRadius="full"
      bg="ink"
      color="onInk"
      align="center"
      justify="center"
      flexShrink={0}
      boxShadow="0 6px 20px rgba(0,0,0,0.18)"
      transition="transform 180ms cubic-bezier(0.34, 1.4, 0.64, 1)"
      transform={open ? 'rotate(90deg)' : 'none'}
    >
      <Icon as={open ? X : Sparkles} boxSize={5} />
    </Flex>
    </Box>
  )
}

function promptsFor(role, currentStepNumber) {
  if (atLeast(role, 'owner')) {
    return [
      { label: 'Is this actually working?', text: 'How do I tell whether this training is actually changing anything on my floor?' },
      { label: 'What should I expect?', text: 'What should I realistically expect from this in the first ninety days?' },
      { label: 'My manager is not signing anyone off', text: 'Nobody on my floor has been signed off in weeks. How do I raise that with my sales manager?' },
    ]
  }
  if (atLeast(role, 'manager')) {
    return [
      { label: 'Is this person ready?', text: 'How do I decide whether somebody is genuinely ready to be signed off on a step?' },
      { label: 'Feedback that lands', text: 'One of my salespeople keeps making the same mistake. How do I give feedback that actually changes it?' },
      { label: 'My floor is stuck', text: 'Most of my floor is stuck on the same step. What does that usually mean?' },
    ]
  }
  return [
    currentStepNumber
      ? { label: `Drill Step ${currentStepNumber}`, to: `/coach/?practice=${currentStepNumber}` }
      : { label: 'Run me a drill', to: '/coach/?practice=1' },
    { label: '"I need to think about it"', text: 'A customer just told me they need to think about it. What do I say?' },
    { label: 'What should I work on?', text: 'What should I work on today?' },
  ]
}

export default function CoachDock({ currentStepNumber = null }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { role, isAuthenticated } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const reduceMotion = usePrefersReducedMotion()
  const [hovered, setHovered] = useState(false)

  // Not on the coach itself, not on the public report, not signed out.
  const hidden =
    !isAuthenticated ||
    location.pathname.startsWith('/coach/') ||
    location.pathname.startsWith('/report/')

  if (hidden) return null

  const prompts = promptsFor(role, currentStepNumber)

  const go = (p) => {
    onClose()
    if (p.to) {
      navigate(p.to)
      return
    }
    // The question travels with them, so the coach opens already asked rather
    // than staring at an empty box.
    navigate(`/coach/?ask=${encodeURIComponent(p.text)}`)
  }

  return (
    <>
      <Box
        position="fixed"
        right={{ base: 4, lg: 8 }}
        bottom={{ base: 'calc(6.5rem + env(safe-area-inset-bottom))', lg: 8 }}
        zIndex={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <HStack
          as="button"
          onClick={onOpen}
          aria-label="Ask the coach"
          spacing={0}
          bg="transparent"
          _focusVisible={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '4px', borderRadius: '999px' }}
        >
          {/* The label only exists on a pointer device, where there is room and
              a hover to reveal it. On a phone the mark speaks for itself. */}
          <Box
            display={{ base: 'none', lg: 'block' }}
            overflow="hidden"
            maxW={hovered && !reduceMotion ? '150px' : '0px'}
            opacity={hovered ? 1 : 0}
            transition={reduceMotion ? undefined : 'max-width 220ms ease, opacity 160ms ease'}
          >
            <Text
              bg="ink"
              color="onInk"
              fontSize="body-sm"
              fontWeight={600}
              whiteSpace="nowrap"
              pl={4}
              pr={3}
              py={2.5}
              borderLeftRadius="full"
            >
              Ask the coach
            </Text>
          </Box>
          <CoachMark open={isOpen} />
        </HStack>
      </Box>

      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="blackAlpha.500" />
        <DrawerContent
          bg="card"
          // FULL HEIGHT ON A PHONE, a floating card on a desktop.
          //
          // It was a bottom sheet sized to its content, so on a phone the coach
          // got a third of the screen and the conversation scrolled inside a
          // slot while two thirds of the display showed the page behind it. The
          // coach is the product on a phone and it should have the phone.
          //
          // 100dvh rather than 100vh: vh is the viewport with the browser chrome
          // hidden, so on iOS Safari the composer sits under the address bar
          // until you scroll, which is exactly where the send button lives.
          h={{ base: '100dvh', lg: 'auto' }}
          maxH={{ base: '100dvh', lg: '75vh' }}
          borderTopRadius={{ base: 0, lg: 'cardLg' }}
          pb="calc(1rem + env(safe-area-inset-bottom))"
          borderRadius={{ lg: 'cardLg' }}
          // On desktop this is meant to be a card floating above the dock
          // rather than a full width sheet. It used to say maxW 420 with an
          // auto left margin, and Chakra's own placement styles beat the maxW,
          // so the content stayed 100vw wide. An auto left margin on a box that
          // is already exactly as wide as its container, with 2rem asked for on
          // the right, resolves to MINUS 2rem, and the panel hung off the left
          // edge of the screen with its first two characters unreadable.
          //
          // Placed explicitly instead. left auto stops the box being stretched
          // between left 0 and right 0, so the width is the width and there is
          // no over-constrained box for a margin to silently absorb. The
          // important flags are here because we are overriding a component
          // library's own placement rules, which is the one case that earns
          // them. 62em is Chakra's lg.
          sx={{
            '@media (min-width: 62em)': {
              left: 'auto !important',
              right: '2rem !important',
              width: '420px !important',
              maxWidth: 'calc(100vw - 4rem) !important',
              marginLeft: '0 !important',
              marginRight: '0 !important',
              marginBottom: '2rem !important',
            },
          }}
        >
          <DrawerBody px={5} pt={4} pb={2}>
            {/* The way out. The grab handle alone was the only affordance for
                closing this, which is a gesture you have to already know, and it
                stopped meaning anything once the panel filled the screen. */}
            <HStack justify="space-between" align="center" mb={3} display={{ lg: 'none' }}>
              <Box w="36px" h="4px" bg="line" borderRadius="full" aria-hidden="true" />
              <IconButton
                aria-label="Close the coach"
                icon={<X size={18} />}
                variant="ghost"
                size="sm"
                minW="44px"
                h="44px"
                onClick={onClose}
              />
            </HStack>

            <HStack spacing={2.5} mb={1}>
              <Box
                border="1.5px solid"
                borderColor="inkMuted"
                borderRadius="5px"
                px={2}
                py={0.5}
                fontFamily="mono"
                fontSize="11px"
                fontWeight={700}
                letterSpacing="0.06em"
                color="ink"
              >
                J<Box as="span" color="accent.500" px="1px">|</Box>13
              </Box>
              <Text textStyle="eyebrow" color="inkMuted">
                Coach
              </Text>
            </HStack>

            <Text fontSize="body" color="inkMuted" lineHeight={1.6} mb={4}>
              Ask anything, or start here.
            </Text>

            <VStack align="stretch" spacing={2} mb={3}>
              {prompts.map((p) => (
                <HStack
                  key={p.label}
                  as="button"
                  onClick={() => go(p)}
                  bg="surface"
                  borderRadius="card"
                  px={4}
                  py={3.5}
                  spacing={3}
                  textAlign="left"
                  _hover={{ bg: 'surface2' }}
                  _active={{ transform: 'scale(0.99)' }}
                  transition="background 120ms ease"
                  minH="52px"
                >
                  <Icon as={Sparkles} boxSize={3.5} color="accent.500" flexShrink={0} />
                  <Text fontSize="body-sm" color="ink" fontWeight={500}>
                    {p.label}
                  </Text>
                </HStack>
              ))}
            </VStack>

            <Text
              as="button"
              onClick={() => {
                onClose()
                navigate('/coach/')
              }}
              fontSize="body-sm"
              color="inkMuted"
              fontWeight={600}
              w="full"
              py={3}
              _hover={{ color: 'ink' }}
            >
              Open the full coach
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
